import { addMonths, isBefore, isSameDay, startOfMonth } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { asDate } from "./as-date";
import type { DateRange } from "./date-range";
import type { MonthNumber } from "./months";
import type { TimeRange } from "./time-range";
import type { Timezone } from "./timezone";
import { tz } from "./timezone";
import type { YearAndMonth } from "./year-and-month";

/**
 * Returns an array of YearAndMonth objects representing the months between the start and end of the provided date range.
 * E.g. if the date range is from 2021-01-10 to 2021-03-19, the result will be an array of YearAndMonth objects
 * representing January, February and March of 2021.
 */
function getMonthsAndYearsOf(
    dateRange: DateRange,
    timeZone: Timezone,
): YearAndMonth[];
/**
 * Returns an array of YearAndMonth objects representing the months between the start and end of the provided date range.
 * E.g. if the date range is from 2021-01-10 to 2021-03-19, the result will be an array of YearAndMonth objects
 * representing January, February and March of 2021.
 */
function getMonthsAndYearsOf(
    timeRange: TimeRange,
    timeZone: Timezone,
): YearAndMonth[];
/**
 * Returns an array of YearAndMonth objects representing the months between the start and end of the provided date range.
 * E.g. if the date range is from 2021-01-10 to 2021-03-19, the result will be an array of YearAndMonth objects
 * representing January, February and March of 2021.
 */
function getMonthsAndYearsOf(
    range: DateRange | TimeRange,
    timeZone?: Timezone,
): YearAndMonth[] {
    const _timeZone = timeZone || tz.EuropeStockholm;
    const startDate = startOfMonth(asDate(range.start));
    const endDate = startOfMonth(asDate(range.end));

    const months: YearAndMonth[] = [];

    // Convert start and end dates to the provided time zone
    let currentZoned = toZonedTime(startDate, _timeZone);
    const endZoned = toZonedTime(endDate, _timeZone);

    // Iterate through months while current is before the end date or on the same day as the end date
    while (
        isBefore(currentZoned, endZoned) ||
        isSameDay(currentZoned, endZoned)
    ) {
        months.push({
            year: currentZoned.getFullYear(),
            month: currentZoned.getMonth() as MonthNumber,
        });
        // Move to the next month, whilst ensuring the time zone is respected
        currentZoned = toZonedTime(addMonths(currentZoned, 1), _timeZone);
    }

    return months;
}

export { getMonthsAndYearsOf };
